import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for frontend error tracking and performance monitoring
 */
export function initSentry() {
  // Only initialize if DSN is configured
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  
  if (!dsn) {
    console.warn("[Sentry] No DSN configured - error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: dsn,
    
    // Integrations for React and browser
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false, // Don't mask text for better debugging
        blockAllMedia: false,
      }),
    ],
    
    // Performance Monitoring
    // Capture 10% of transactions in production, 100% in development
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
    
    // Session Replay
    // Capture 10% of all sessions
    replaysSessionSampleRate: 0.1,
    // Capture 100% of sessions where errors occur
    replaysOnErrorSampleRate: 1.0,
    
    // Environment identification
    environment: process.env.NODE_ENV || "development",
    
    // Release tracking
    release: process.env.REACT_APP_VERSION || "1.0.0",
    
    // Trace propagation for backend correlation
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.squidemy\.com/,
      /^https:\/\/.*\.emergentagent\.com/,
      /^https:\/\/squidemy\.com/,
    ],
    
    // Filter out known non-critical errors
    ignoreErrors: [
      // Browser extensions
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network errors that users can't control
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      // Third-party script errors
      /^Script error\.?$/,
      // Chrome specific
      "Non-Error promise rejection captured",
    ],
    
    // Before sending events
    beforeSend(event, hint) {
      // Don't send errors from ad services or analytics
      const url = event.request?.url || "";
      if (
        url.includes("adservice") ||
        url.includes("doubleclick") ||
        url.includes("googleads") ||
        url.includes("analytics")
      ) {
        return null;
      }
      
      // Don't send errors if user is on an old cached version
      if (hint.originalException?.message?.includes("Loading chunk")) {
        // Chunk loading errors - likely outdated cache
        return null;
      }
      
      return event;
    },
  });
  
  console.log("[Sentry] Initialized for", process.env.NODE_ENV);
}

/**
 * Set user context for Sentry
 * Call this after user logs in
 */
export function setSentryUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  
  Sentry.setUser({
    id: user.user_id,
    email: user.email,
    username: user.name,
  });
}

/**
 * Clear user context from Sentry
 * Call this on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(message, data = {}, level = "info") {
  Sentry.addBreadcrumb({
    message,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture an exception manually
 */
export function captureException(error, context = {}) {
  Sentry.captureException(error, {
    tags: context.tags || {},
    extra: context.extra || {},
  });
}

/**
 * Capture a message
 */
export function captureMessage(message, level = "info") {
  Sentry.captureMessage(message, level);
}

// Export Sentry for direct use
export { Sentry };
